import { withErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { FontWeightBold } from 'autogen/design-tokens/global/tokens';
import { Spacing16 } from 'autogen/design-tokens/tokens';
import { FrontPage } from 'autogen/translation-keys/trans-website-articleuniverse-sectionnavigationmenu';
import LinkActionButton from 'components/Button/LinkActionButton';
import ContainerFluid from 'components/Layout/components/ContainerFluid';
import { addScrollPadding, invisibleScrollbar, removeListStyling } from 'modules/helpers/mixins';
import type { SectionNavigationType } from '../../shared/types';
import { frontPageId } from '../../shared/variables';

// #region Styles
const StyledContainerFluid = styled(ContainerFluid)`
    padding-left: 0;
    padding-right: 0;
`;

const NavigationWrapper = styled.nav`
    padding-top: ${Spacing16};
    padding-bottom: ${Spacing16};
`;

const NavigationList = styled.ul`
    ${removeListStyling}
    ${invisibleScrollbar}
    ${addScrollPadding()}
    display: flex;
    gap: ${Spacing16};
    overflow: auto;
    max-width: 100%;
`;

const ListItem = styled.li`
    flex: 1 0 auto;
`;

const StyledLinkActionButton = styled(LinkActionButton)<{ $isActive?: boolean }>`
    width: 100%;

    ${({ $isActive }) =>
        $isActive &&
        css`
            font-weight: ${FontWeightBold};
        `}
`;
// #endregion Styles

function SectionNavigation({ sections, currentSectionId, frontPageUrl }: SectionNavigationType) {
    const { t } = useTranslation();

    return (
        <StyledContainerFluid>
            <NavigationWrapper>
                <NavigationList>
                    {frontPageUrl && (
                        <ListItem>
                            <StyledLinkActionButton
                                variant="negative"
                                action={frontPageUrl}
                                $isActive={currentSectionId === frontPageId}
                            >
                                {t(FrontPage)}
                            </StyledLinkActionButton>
                        </ListItem>
                    )}

                    {sections.map((section) => {
                        const { entryId, publicName, linkAction } = section;
                        if (!linkAction) return null;
                        return (
                            <ListItem key={entryId}>
                                <StyledLinkActionButton
                                    variant="negative"
                                    action={linkAction}
                                    $isActive={entryId === currentSectionId}
                                >
                                    {publicName}
                                </StyledLinkActionButton>
                            </ListItem>
                        );
                    })}
                </NavigationList>
            </NavigationWrapper>
        </StyledContainerFluid>
    );
}

export default withErrorBoundary(SectionNavigation, {
    fallback: null,
    onError(error, info) {
        console.error(error, info);
    }
});
